import React, { createContext, useContext, useState } from "react";
import {
  Alert,
  AlertActions,
  AlertDescription,
  AlertTitle,
} from "../design/alert";
import { Button } from "../design/button";

interface DeleteAlertContextValue<T = any> {
  openAlert: (
    entity: T,
    config: {
      displayName: string;
      onDelete: (id: string) => Promise<void>;
      getId: (entity: T) => string;
    },
  ) => void;
  closeAlert: () => void;
  isOpen: boolean;
  entity: T | undefined;
}

const DeleteAlertContext = createContext<DeleteAlertContextValue | undefined>(
  undefined,
);

export const DeleteAlertProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [entity, setEntity] = useState<any | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [displayName, setDisplayName] = useState<string>("");
  const [onDelete, setOnDelete] = useState<(id: string) => Promise<void>>();
  const [getId, setGetId] = useState<(entity: any) => string>();
  const [isDeleting, setIsDeleting] = useState(false);

  const openAlert = (
    entity: any,
    config: {
      displayName: string;
      onDelete: (id: string) => Promise<void>;
      getId: (entity: any) => string;
    },
  ) => {
    setEntity(entity);
    setDisplayName(config.displayName);
    setOnDelete(() => config.onDelete);
    setGetId(() => config.getId);
    setIsOpen(true);
  };

  const closeAlert = () => {
    setIsOpen(false);
    setEntity(undefined);
    setDisplayName("");
    setOnDelete(undefined);
    setGetId(undefined);
  };

  const handleYes = async () => {
    if (!entity || !onDelete || !getId) return;
    setIsDeleting(true);
    try {
      await onDelete(getId(entity));
      closeAlert();
    } catch (err) {
      console.error("Delete failed:", err);
      alert(`Failed to delete ${displayName}. Please try again later.`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <DeleteAlertContext.Provider
      value={{ openAlert, closeAlert, isOpen, entity }}
    >
      {entity && (
        <Alert open={isOpen} onClose={closeAlert}>
          <AlertTitle>Delete {displayName}?</AlertTitle>
          <AlertDescription>
            Are you sure you want to delete this {displayName}? This action
            cannot be undone.
          </AlertDescription>
          <AlertActions>
            <Button plain onClick={closeAlert}>
              Cancel
            </Button>
            <Button disabled={isDeleting} color="red" onClick={handleYes}>
              Yes
            </Button>
          </AlertActions>
        </Alert>
      )}
      {children}
    </DeleteAlertContext.Provider>
  );
};

export const useDeleteAlert = () => {
  const ctx = useContext(DeleteAlertContext);
  if (!ctx) {
    throw new Error("useDeleteAlert must be used inside DeleteAlertProvider");
  }
  return ctx;
};
